// Shared draft assembly + persistence. A "draft" is the temporal save of all
// in-progress edits plus audit progress, written to IndexedDB keyed by folder
// signature. Built identically by the Next autosave, the Pending button, and
// the top-bar Quit flow so all three round-trip through restoreFromDraft.

import { writeDraft } from '../io/drafts';
import type { AppState } from '../store';
import { episodeElapsedMs } from '../store/video';
import type { Draft } from '../types';
import { buildCompactOutput } from './compactOutput';

/** Assemble the current store state into a Draft (edits for all four sides +
 *  the audited-frame set). The per-side header fields are informational —
 *  restoreFromDraft only reads `edits` / `audited`. */
export function buildDraft(s: AppState): Draft {
  const header = {
    image_size: s.imageSize,
    fps: s.fps,
    num_frames: s.totalFrames,
    video: s.folder?.name ?? '',
  };
  return {
    front_left: buildCompactOutput('front_left', header, s.editsFrontLeft),
    front_right: buildCompactOutput('front_right', header, s.editsFrontRight),
    left: buildCompactOutput('left', header, s.editsLeft),
    right: buildCompactOutput('right', header, s.editsRight),
    audited: [...s.auditedFrames],
    elapsedMs: episodeElapsedMs(s),
    poseSeedSource: s.poseSeedSource,
    savedAt: new Date().toISOString(),
  };
}

/** Build and persist the draft for the active folder. No-op (with a warning)
 *  when no folder signature is available. Never throws — write failures are
 *  logged, not propagated, so callers can navigate away regardless. */
export async function saveDraft(s: AppState): Promise<void> {
  if (!s.folderSig) {
    console.warn('folderSig is null; draft not saved.');
    return;
  }
  try {
    await writeDraft(s.folderSig, buildDraft(s));
  } catch (err) {
    console.warn('Draft write failed:', err);
  }
}
